import React, { useEffect, useState, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPinIcon, CheckIcon, NavigationIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
// Fix Leaflet default icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});
interface LocationPickerProps {
  onConfirm: (googleMapsUrl: string) => void;
  initialLocation?: string; // Optional existing Google Maps URL
}
const DEFAULT_CENTER: [number, number] = [9.9281, -84.0907]; // San José, Costa Rica
export function LocationPicker({
  onConfirm,
  initialLocation
}: LocationPickerProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const [isLocating, setIsLocating] = useState(true);
  const [center, setCenter] = useState<[number, number]>(DEFAULT_CENTER);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const {
    t
  } = useLanguage();
  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    // Create map
    const map = L.map(mapRef.current, {
      center: DEFAULT_CENTER,
      zoom: 15,
      zoomControl: false // We'll add it to bottom right to not interfere with the center pin
    });
    L.control.zoom({
      position: 'bottomright'
    }).addTo(map);
    // Add tile layer (OpenStreetMap)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19
    }).addTo(map);
    mapInstanceRef.current = map;
    // Update center state when user pans the map
    map.on('moveend', () => {
      const newCenter = map.getCenter();
      setCenter([newCenter.lat, newCenter.lng]);
      setIsConfirmed(false); // Reset confirmation if they move the map
    });
    // Try to get user's location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        const {
          latitude,
          longitude
        } = position.coords;
        map.setView([latitude, longitude], 16);
        setCenter([latitude, longitude]);
        setIsLocating(false);
      }, error => {
        console.error('Geolocation error:', error);
        setIsLocating(false);
        // Default to Costa Rica center
      }, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      });
    } else {
      setIsLocating(false);
    }
    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);
  const handleRecenter = () => {
    if (!navigator.geolocation || !mapInstanceRef.current) return;
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(position => {
      const {
        latitude,
        longitude
      } = position.coords;
      mapInstanceRef.current?.setView([latitude, longitude], 16);
      setCenter([latitude, longitude]);
      setIsLocating(false);
    }, () => setIsLocating(false), {
      enableHighAccuracy: true
    });
  };
  const handleConfirm = () => {
    const url = `https://maps.google.com/?q=${center[0]},${center[1]}`;
    setIsConfirmed(true);
    onConfirm(url);
  };
  return <div className="flex flex-col gap-3" data-id="element-989">
      <div className="relative w-full h-[250px] rounded-xl overflow-hidden border border-gray-700 shadow-sm" data-id="element-990">
        {/* The Map Container */}
        <div ref={mapRef} className="w-full h-full z-0" data-id="element-991" />

        {/* Fixed Center Pin Overlay (Better UX for mobile than draggable marker) */}
        <div className="absolute inset-0 pointer-events-none z-10 flex items-center justify-center" data-id="element-992">
          <div className="relative -mt-8" data-id="element-993">
            {' '}
            {/* Offset to point exactly at center */}
            <MapPinIcon className="w-8 h-8 text-terracota drop-shadow-md" fill="currentColor" data-id="element-994" />
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-1 bg-black/20 rounded-[100%] blur-[1px]" data-id="element-995" />
          </div>
        </div>

        {/* Recenter Button */}
        <button onClick={handleRecenter} className="absolute top-3 right-3 z-20 bg-surface p-2.5 rounded-full shadow-md text-gray-300 hover:text-white border border-gray-700 transition-colors active:scale-95" aria-label="Mi ubicación actual" data-id="element-996">
          <NavigationIcon className={`w-5 h-5 ${isLocating ? 'animate-pulse text-verde' : ''}`} data-id="element-997" />
        </button>

        {/* Loading Overlay */}
        {isLocating && <div className="absolute inset-0 z-30 bg-carbon/60 backdrop-blur-[2px] flex items-center justify-center" data-id="element-998">
            <div className="bg-surface px-4 py-2 rounded-full shadow-md text-sm font-medium text-white flex items-center gap-2 border border-gray-700" data-id="element-999">
              <div className="w-4 h-4 border-2 border-verde border-t-transparent rounded-full animate-spin" data-id="element-1000" />
              {t('searchingLocation')}
            </div>
          </div>}
      </div>

      <div className="flex items-center justify-between gap-3" data-id="element-1001">
        <p className="text-xs text-gray-400 leading-tight flex-1" data-id="element-1002">
          {t('moveMapHint')}
        </p>
        <button onClick={handleConfirm} className={`px-5 py-2.5 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all active:scale-95 whitespace-nowrap ${isConfirmed ? 'bg-verde/10 text-verde border border-verde/20' : 'bg-terracota text-white shadow-md shadow-terracota/20 hover:bg-terracota/90 border border-white/10'}`} data-id="element-1003">
          {isConfirmed ? <>
              <CheckIcon className="w-4 h-4" data-id="element-1004" /> {t('saved')}
            </> : t('confirmLocation')}
        </button>
      </div>
    </div>;
}